import React, { useState } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
} from 'react-native';
import {
    DrawerContentScrollView,
    DrawerContentComponentProps,
} from '@react-navigation/drawer';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { theme } from '../constants/theme';
import { useAuth } from '../contexts/AuthContext';
import { AppModal, ModalType } from './AppModal';

const CustomDrawerContent = (props: DrawerContentComponentProps) => {
    const { user, logout } = useAuth();

    const getInitials = (fullName: string): string => {
        const names = fullName.split(' ');
        if (names.length >= 2) {
            return `${names[0][0]}${names[1][0]}`.toUpperCase();
        }
        return fullName.substring(0, 2).toUpperCase();
    };

    const [modal, setModal] = useState<{
        visible: boolean;
        type: ModalType;
        title: string;
        message: string;
        onConfirm?: () => void;
        confirmText?: string;
    }>({ visible: false, type: 'info', title: '', message: '' });

    const handleLogout = () => {
        setModal({
            visible: true,
            type: 'confirm',
            title: 'Logout',
            message: 'Are you sure you want to logout?',
            confirmText: 'Logout',
            onConfirm: async () => {
                setModal(prev => ({ ...prev, visible: false }));
                await logout();
            },
        });
    };

    const MenuItem = ({ label, icon, onPress, showChevron = false }: { label: string; icon: string; onPress: () => void; showChevron?: boolean }) => (
        <TouchableOpacity style={styles.menuItem} onPress={onPress} activeOpacity={0.7}>
            <Icon name={icon} size={24} color={theme.colors.text} style={styles.menuIcon} />
            <Text style={styles.menuLabel}>{label}</Text>
            {showChevron && <Icon name="chevron-right" size={20} color={theme.colors.textLight} />}
        </TouchableOpacity>
    );

    return (
        <View style={styles.container}>
            <DrawerContentScrollView {...props} contentContainerStyle={styles.scrollContent}>
                {/* Header with Curved Background */}
                <View style={styles.headerContainer}>
                    <View style={styles.curvedBackground} />
                    <View style={styles.profileContent}>
                        <View style={styles.avatar}>
                            <Text style={styles.avatarText}>
                                {user ? getInitials(user.fullName) : 'UN'}
                            </Text>
                        </View>
                        <Text style={styles.userName}>{user?.fullName || 'User'}</Text>
                        <Text style={styles.userEmail}>{user?.username}</Text>
                    </View>
                </View>

                {/* Menu Items */}
                <View style={styles.menuSection}>
                    <MenuItem
                        label="Home"
                        icon="home"
                        onPress={() => {
                            props.navigation.closeDrawer();
                            props.navigation.navigate('Dashboard'); // Assuming 'Dashboard' is the home screen
                        }}
                    />

                    <MenuItem
                        label="Dairy Purchases"
                        icon="cart"
                        onPress={() => {
                            props.navigation.closeDrawer();
                            props.navigation.navigate('DairyPurchases');
                        }}
                    />

                    {user?.role === 'admin' && (
                        <>
                            <MenuItem
                                label="Activity Log"
                                icon="clipboard-text-clock"
                                onPress={() => {
                                    props.navigation.closeDrawer();
                                    props.navigation.navigate('ActivityLog');
                                }}
                            />
                            <MenuItem
                                label="Employee Salary"
                                icon="account-cash"
                                onPress={() => {
                                    props.navigation.closeDrawer();
                                    props.navigation.navigate('EmployeeList');
                                }}
                            />
                        </>
                    )}

                    <MenuItem
                        label="Change Password"
                        icon="shield-key"
                        onPress={() => {
                            props.navigation.closeDrawer();
                            props.navigation.navigate('ChangePassword');
                        }}
                    />
                </View>

            </DrawerContentScrollView>

            {/* Bottom Logout Button */}
            <View style={styles.bottomSection}>
                <TouchableOpacity
                    style={styles.logoutButton}
                    onPress={handleLogout}
                    activeOpacity={0.8}>
                    <Text style={styles.logoutText}>Logout</Text>
                </TouchableOpacity>
                <Text style={styles.versionText}>v1.0.3</Text>
            </View>

            <AppModal
                visible={modal.visible}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onClose={() => setModal(prev => ({ ...prev, visible: false }))}
                onConfirm={modal.onConfirm}
                confirmText={modal.confirmText || 'OK'}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#FFFFFF',
    },
    scrollContent: {
        paddingTop: 0,
    },
    headerContainer: {
        alignItems: 'center',
        marginBottom: theme.spacing.xl,
        position: 'relative',
        paddingTop: 60, // Space for the curve content
        paddingBottom: 20,
    },
    curvedBackground: {
        position: 'absolute',
        top: -300,
        left: -100,
        right: -100,
        height: 450,
        backgroundColor: theme.colors.primary, // Brand color
        borderRadius: 400, // Creates the curve effect
        transform: [{ scaleX: 1.2 }],
    },
    profileContent: {
        alignItems: 'center',
        zIndex: 1,
    },
    avatar: {
        width: 100,
        height: 100,
        borderRadius: 50,
        backgroundColor: theme.colors.surface,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: theme.spacing.sm,
        borderWidth: 3,
        borderColor: theme.colors.surface,
        ...theme.shadows.sm,
    },
    avatarText: {
        fontSize: 36,
        color: theme.colors.primary,
        fontWeight: 'bold',
    },
    userName: {
        ...theme.typography.h6,
        color: theme.colors.text,
        fontWeight: '700',
        marginTop: theme.spacing.xs,
    },
    userEmail: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
    },
    menuSection: {
        paddingHorizontal: theme.spacing.lg,
    },
    menuItem: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: theme.spacing.md,
        marginBottom: theme.spacing.xs,
    },
    menuIcon: {
        marginRight: theme.spacing.lg,
        color: theme.colors.text,
    },
    menuLabel: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '500',
        fontSize: 16,
        flex: 1,
    },
    bottomSection: {
        padding: theme.spacing.lg,
        paddingBottom: theme.spacing.xl,
    },
    logoutButton: {
        backgroundColor: theme.colors.primary, // Brand color
        paddingVertical: 12,
        borderRadius: 12, // Rounded corners
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        marginBottom: theme.spacing.md,
        ...theme.shadows.sm,
    },
    logoutText: {
        color: theme.colors.surface,
        fontSize: 16,
        fontWeight: '600',
    },
    versionText: {
        textAlign: 'center',
        color: theme.colors.textLight,
        fontSize: 12,
    },
});

export default CustomDrawerContent;
